/**
 * Add User Dialog Component
 * Dialog for adding users to TotalSERP resource and optionally assigning roles
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useToast } from '../context/ToastContext';
import { assignUserToResource } from '../services/usersService';

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  existingUserEmails: string[];
  onUsersAdded: () => void;
}

const AddUserDialog: React.FC<AddUserDialogProps> = ({
  open,
  onClose,
  existingUserEmails,
  onUsersAdded,
}) => {
  const { showToast } = useToast();
  const [emails, setEmails] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Parse emails from input (comma or newline separated)
  const parseEmails = (input: string): string[] => {
    return input
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Get parsed and validated emails
  const getParsedEmails = useCallback(() => {
    return parseEmails(emails);
  }, [emails]);

  // Validate all emails
  const validateEmails = useCallback(() => {
    const parsedEmails = getParsedEmails();
    
    if (parsedEmails.length === 0) {
      return 'Please enter at least one email address';
    }

    // Check for invalid emails
    const invalidEmails = parsedEmails.filter(email => !isValidEmail(email));
    if (invalidEmails.length > 0) {
      return `Invalid email format: ${invalidEmails.join(', ')}`;
    }

    // Check for duplicate emails in input
    const duplicates = parsedEmails.filter((email, index) => 
      parsedEmails.indexOf(email) !== index
    );
    if (duplicates.length > 0) {
      return `Duplicate emails in input: ${duplicates.join(', ')}`;
    }

    // Check for emails that already exist in the system
    const existingEmails = parsedEmails.filter(email => 
      existingUserEmails.includes(email.toLowerCase())
    );
    if (existingEmails.length > 0) {
      return `Users already exist: ${existingEmails.join(', ')}`;
    }

    return null;
  }, [getParsedEmails, existingUserEmails]);

  const handleAdd = async () => {
    setError(null);

    // Validate emails
    const validationError = validateEmails();
    if (validationError) {
      setError(validationError);
      return;
    }

    const parsedEmails = getParsedEmails();
    setLoading(true);

    try {
      const results = [];
      
      // Add each user to the resource
      for (const email of parsedEmails) {
        try {
          // Assign to resource (serp)
          await assignUserToResource(email, 'serp', 'serp', 'application');
          
          results.push({ email, success: true });
          showToast(`Successfully added ${email}`, 'success');
        } catch (userError: any) {
          console.error(`Failed to add user ${email}:`, userError);
          const errorMessage = userError.response?.data?.message || userError.message || 'Unknown error';
          results.push({ email, success: false, error: errorMessage });
          showToast(`Failed to add ${email}: ${errorMessage}`, 'error');
        }
      }

      // Check if any users were successfully added
      const successCount = results.filter(r => r.success).length;
      if (successCount > 0) {
        // Reload the users list
        onUsersAdded();
        
        // Close dialog if all users were added successfully
        if (successCount === parsedEmails.length) {
          handleClose();
        } else {
          // Some failed, keep dialog open with error message
          const failedEmails = results.filter(r => !r.success).map(r => r.email);
          setError(`Failed to add: ${failedEmails.join(', ')}`);
        }
      } else {
        setError('Failed to add all users');
      }
    } catch (error: any) {
      console.error('Error adding users:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add users';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmails('');
      setError(null);
      onClose();
    }
  };

  const emailList = getParsedEmails();
  const validationError = validateEmails();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Users to TotalSERP</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter email addresses (one per line or comma-separated)
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Email Addresses"
            placeholder="user1@example.com&#10;user2@example.com, user3@example.com"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {emailList.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {emailList.length} email(s) entered:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {emailList.map((email, index) => (
                  <Chip
                    key={index}
                    label={email}
                    size="small"
                    color={isValidEmail(email) ? 'primary' : 'error'}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <PersonAddIcon />}
          disabled={loading || !!validationError || emailList.length === 0}
        >
          {loading ? 'Adding...' : `Add ${emailList.length} User${emailList.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserDialog;
